// ======================================================================
// 📁 File: src/modules/ppe/pages/engineering/boq/BOQMasterList.jsx
// ======================================================================

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axiosClient from "../../../../../api/axiosClient";

import BOQKpiBar from "./BOQKpiBar";

export default function BOQMasterList() {
  const navigate = useNavigate();

  // ------------------------------------------------------------------
  // STATE
  // ------------------------------------------------------------------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [productFamilies, setProductFamilies] = useState([]);
  const [pfMap, setPfMap] = useState({});

  // ✅ NEW STATES
  const [productVariants, setProductVariants] = useState([]);
  const [variantId, setVariantId] = useState("ALL");

  const [equipments, setEquipments] = useState([]);
  const [equipmentId, setEquipmentId] = useState("ALL");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [productFamilyId, setProductFamilyId] = useState("ALL");

  // ------------------------------------------------------------------
  // LOAD PRODUCT FAMILIES
  // ------------------------------------------------------------------
  const loadProductFamilies = useCallback(async () => {
    try {
      const res = await axiosClient.get(
        "/api/ppe/masters/product-families"
      );

      const data = res.data?.data || [];
      setProductFamilies(data);

      const map = {};
      data.forEach((pf) => {
        map[pf.id] = pf.name;
      });
      setPfMap(map);
    } catch (err) {
      console.error("Failed to load product families", err);
    }
  }, []);

  // ------------------------------------------------------------------
  // LOAD PRODUCT VARIANTS (DEPENDENT)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (productFamilyId === "ALL") {
      setProductVariants([]);
      return;
    }

    axiosClient
      .get(
        `/api/ppe/masters/product-variants?product_family_id=${productFamilyId}`
      )
      .then((res) => {
        setProductVariants(res.data?.data || []);
      })
      .catch(() => setProductVariants([]));
  }, [productFamilyId]);

  // ------------------------------------------------------------------
  // LOAD EQUIPMENT
  // ------------------------------------------------------------------
  useEffect(() => {
    axiosClient
      .get("/api/ppe/masters/equipment")
      .then((res) => {
        setEquipments(res.data?.data || []);
      })
      .catch(() => setEquipments([]));
  }, []);

  // ------------------------------------------------------------------
  // LOAD BOQ MASTERS
  // ------------------------------------------------------------------
  const loadBOQMasters = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};

      if (productFamilyId !== "ALL") {
        params.product_family_id = productFamilyId;
      }

      const res = await axiosClient.get(
        "/api/ppe/orders/boq-master",
        { params }
      );

      setRows(res.data?.data || []);
    } catch (err) {
      console.error("Failed to load BOQ masters", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [productFamilyId]);

  useEffect(() => {
    loadProductFamilies();
    loadBOQMasters();
  }, [loadProductFamilies, loadBOQMasters]);

  // ------------------------------------------------------------------
  // STATUS DERIVATION
  // ------------------------------------------------------------------
  const resolveStatus = (row) => {
    if (!row.is_active) return "INACTIVE";
    if (row.status_code === "SUBMITTED") return "SUBMITTED";
    if (row.is_approved) return "APPROVED";
    return "DRAFT";
  };

  // ------------------------------------------------------------------
  // FILTERING
  // ------------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      r.boq_code?.toLowerCase().includes(q) ||
      r.boq_name?.toLowerCase().includes(q);

    const matchesStatus =
      status === "ALL" || resolveStatus(r) === status;

    const matchesVariant =
      variantId === "ALL" ||
      String(r.product_variant_id) === String(variantId);

    const matchesEquipment =
      equipmentId === "ALL" ||
      String(r.equipment_id) === String(equipmentId);

    return (
      matchesSearch &&
      matchesStatus &&
      matchesVariant &&
      matchesEquipment
    );
  });

  // ------------------------------------------------------------------
  // KPI STATS
  // ------------------------------------------------------------------
  const stats = {
    total: rows.length,
    draft: rows.filter((r) => !r.is_approved && r.is_active).length,
    approved: rows.filter((r) => r.is_approved).length,
    inactive: rows.filter((r) => !r.is_active).length,
  };

  // ------------------------------------------------------------------
  // ACTIONS
  // ------------------------------------------------------------------
  const submitBOQ = async (id) => {
    if (!window.confirm("Submit BOQ master for approval?")) return;

    try {
      await axiosClient.post(
        `/api/ppe/orders/boq-masters/${id}/submit`
      );
      await loadBOQMasters();
    } catch (err) {
      alert(
        err?.response?.data?.message ||
        "Failed to submit BOQ master"
      );
    }
  };

  const approveBOQ = async (id) => {
    if (!window.confirm("Approve this BOQ master?")) return;
    await axiosClient.post(
      `/api/ppe/orders/boq-masters/${id}/approve`
    );
    loadBOQMasters();
  };

  const rejectBOQ = async (id) => {
    const reason = window.prompt("Enter rejection reason");
    if (!reason) return;

    await axiosClient.post(
      `/api/ppe/orders/boq-masters/${id}/reject`,
      { reason }
    );
    loadBOQMasters();
  };

  const inactivateBOQ = async (id) => {
    if (!window.confirm("Mark this BOQ master inactive?")) return;

    await axiosClient.post(
      `/api/ppe/orders/boq-master/${id}/inactive`
    );
    loadBOQMasters();
  };

  // ------------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------------
  return (
    <div>
      {/* HEADER */}
      <div
        style={{
          background:
            "linear-gradient(90deg, #0f766e 0%, #14b8a6 50%, #5eead4 100%)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>BOQ Master</h4>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Engineering Bill of Quantity definitions
          </div>
        </div>

        <button
          className="btn btn-light"
          onClick={() =>
            navigate("/ppe/engineering/boq/create")
          }
        >
          + Create BOQ
        </button>
      </div>

      {/* KPI */}
      <BOQKpiBar stats={stats} />

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search by code or name"
          style={{ maxWidth: 240 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* PRODUCT FAMILY */}
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={productFamilyId}
          onChange={(e) => setProductFamilyId(e.target.value)}
        >
          <option value="ALL">All Product Families</option>
          {productFamilies.map((pf) => (
            <option key={pf.id} value={pf.id}>
              {pf.name}
            </option>
          ))}
        </select>

        {/* PRODUCT VARIANT */}
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={variantId}
          onChange={(e) => setVariantId(e.target.value)}
        >
          <option value="ALL">All Variants</option>
          {productVariants.map((pv) => (
            <option key={pv.id} value={pv.id}>
              {pv.variant_code} – {pv.name}
            </option>
          ))}
        </select>

        {/* EQUIPMENT */}
        <select
          className="form-select"
          style={{ maxWidth: 220 }}
          value={equipmentId}
          onChange={(e) => setEquipmentId(e.target.value)}
        >
          <option value="ALL">All Equipment</option>
          {equipments.map((eq) => (
            <option key={eq.id} value={eq.id}>
              {eq.code} – {eq.name}
            </option>
          ))}
        </select>

        {/* STATUS */}
        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="ALL">All Status</option>
          <option value="DRAFT">Draft</option>
          <option value="APPROVED">Approved</option>
          <option value="INACTIVE">Inactive</option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadBOQMasters}
          disabled={loading}
        >
          Apply
        </button>
      </div>

      {/* TABLE */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered mb-0">
            <thead className="table-light">
              <tr>
                <th>BOQ Code</th>
                <th>BOQ Name</th>
                <th>Product Family</th>
                <th>Product Variant</th>
                <th>Equipment</th>
                <th>Version</th>
                <th>Status</th>
                <th style={{ width: 260 }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center">
                    Loading...
                  </td>
                </tr>
              )}

              {!loading && filteredRows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted">
                    No BOQ masters found
                  </td>
                </tr>
              )}

              {filteredRows.map((r) => {
                const derivedStatus = resolveStatus(r);

                return (
                  <tr key={r.id}>
                    <td>{r.boq_code || "-"}</td>
                    <td>{r.boq_name}</td>
                    <td>{pfMap[r.product_family_id] || "-"}</td>
                    <td>{r.product_variant_name || "-"}</td>
                    <td>{r.equipment_name || "-"}</td>
                    <td>v{r.version_no}</td>
                    <td>{derivedStatus}</td>

                    <td style={{ display: "flex", gap: 6 }}>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() =>
                          navigate(
                            `/ppe/engineering/boq/view/${r.id}`
                          )
                        }
                      >
                        View
                      </button>

                      {derivedStatus === "DRAFT" && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              navigate(
                                `/ppe/engineering/boq/edit/${r.id}`
                              )
                            }
                          >
                            Edit
                          </button>

                          <button
                            className="btn btn-sm btn-outline-success"
                            onClick={() => submitBOQ(r.id)}
                          >
                            Submit
                          </button>
                        </>
                      )}

                      {derivedStatus === "SUBMITTED" && (
                        <>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => approveBOQ(r.id)}
                          >
                            Approve
                          </button>

                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => rejectBOQ(r.id)}
                          >
                            Reject
                          </button>
                        </>
                      )}

                      {derivedStatus === "APPROVED" && (
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => inactivateBOQ(r.id)}
                        >
                          Inactivate
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}